import {noop} from '@benchling/js-utils';
import React, {useCallback, useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {getAnalysisOnlyChartViewTypes} from 'analysis-tool/charting/AnalysisChartingConstants';
import {isAnalysisOnlyChartStep} from 'analysis-tool/charting/AnalysisChartingUtils';
import {getVisualizationConfigGQLForTableViewStep} from 'analysis-tool/data/getVisualizationConfigAndName';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {getVisualizationConfigGQLFromChartStepConfig} from 'analysis-tool/data/StepConfigUtils';
import {
  assertViewExists,
  getTitleForView,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {getDefaultChartConfig} from 'analysis-tool/ui/charting/ChartingUtils';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import VisualizationConfigEditor from 'insights/analytics-visualizations/configs/VisualizationConfigEditor';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepConfig,
  PipelineViewGraphQL,
  assertPipelineStepIsStepType,
  pipelineStepIsStepType,
} from 'pipelines/PipelineTypes';
import {
  getPipelineStepRawDatasOfKind,
  getSingleInputRawData,
  tryGetSingleInputRawData,
} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';
import {saveChartOrTable} from '../utils/saveChartOrTable';
import ChartTypeSwitcher from './edit-view/ChartTypeSwitcher';
import {ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE} from './edit-view/chartTypeSwitchUtils';
import EditViewChart from './edit-view/EditViewChart';
import EditViewTransforms from './edit-view/EditViewTransforms';
import RightSidebarSkeleton, {
  RightSidebarWidth,
  RightSidebarConfirmationButton,
} from './RightSidebarSkeleton';
import useMaybeDraftStep, {draftStepGlobalState} from './useMaybeDraftStep';

/** @package */
export const visualizationConfigState = makeUseGlobalClientState<VisualizationConfig | null>(null);

export default function EditViewRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.EDIT_VIEW>): JSX.Element {
  const isDisabled = isAnalysisReadOnly(analysis) || isArchived(analysis);
  const view = getViewForViewReference(analysis, selectedRightSidebarItem.viewReference);
  assertViewExists(view, analysis, selectedRightSidebarItem.viewReference);

  const selectedViewNode = view.lineage.find(
    ({step}) => step.id === selectedRightSidebarItem.viewReference.stepId
  );
  const originalStep = selectedViewNode?.step;
  assert(
    originalStep != null,
    'Expected selectedRightSidebarItem.viewReference.stepId to correspond to a step in the analysis'
  );
  const {draftStep, setDraftStep} = useMaybeDraftStep(originalStep.id, null);

  // getDefaultClientIdForView(view) changes when transforms are added, so we need to use the original step id
  const clientStateId = originalStep.id;
  const [nullableVisualizationConfig, setVisualizationConfig] =
    visualizationConfigState.useGlobalClientState(clientStateId);
  const onChangeVisualizationConfig = (newVisualizationConfig: VisualizationConfig): void => {
    setVisualizationConfig(newVisualizationConfig);
    const visualizationType = getVisualizationType(newVisualizationConfig);
    const stepType =
      visualizationType === VisualizationType.TABLE
        ? PipelineStepType.TABLE_VIEW
        : PipelineStepType.CHART_VIEW;
    const name =
      originalStep.stepType === PipelineStepType.CHART_VIEW
        ? originalStep.config?.chartName
        : originalStep.config?.viewName;

    setDraftStep({
      ...originalStep,
      stepType,
      config:
        stepType === PipelineStepType.CHART_VIEW
          ? {
              chartName: name,
              chartConfig: getPersistedChartViewConfig(newVisualizationConfig),
            }
          : {
              viewName: name,
            },
    });
  };

  const lastNode = view.lineage[view.lineage.length - 1];
  const dataFrames = isViewTerminalStepType(lastNode.step.stepType)
    ? getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: lastNode.step.inputStepDatas.flatMap(({rawDatas}) => rawDatas),
      })
    : getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: lastNode.step.outputStepDatas.flatMap(({rawDatas}) => rawDatas),
      });

  const dataFrameSpec = dataFrames.length > 0 ? dataFrames[0] : null;
  // We need to update the draft step when the input step datas change, so we can use the new data in the preview.
  // We can't just use this in onChangeVisualizationConfig, because the filter/transform section is outside of
  // the visualization config section.
  useEffect(() => {
    if (draftStep != null && !isEqual(draftStep.inputStepDatas, originalStep.inputStepDatas)) {
      setDraftStep({
        ...draftStep,
        inputStepDatas: originalStep.inputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  // We need to update the draft step when the output step datas change for TABLE_VIEW draft steps, because TABLE_VIEW
  // draft steps use the output step datas for their preview.
  // This logic needs to run when there is a draft step and when an original step has finished updating after
  // adding a filter/transform step.
  // This will not wipe out previews for chart views, because chart views do not write output to the draft step.
  useEffect(() => {
    if (
      draftStep != null &&
      !isEqual(draftStep.outputStepDatas, originalStep.outputStepDatas) &&
      originalStep.statusCode === PipelineStepStatusCode.SUCCEEDED
    ) {
      setDraftStep({
        ...draftStep,
        outputStepDatas: originalStep.outputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  const contextValue = useMemo(
    () => ({
      dataFramesInfos: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]:
          dataFrameSpec != null ? [{dataFrameSpec, id: dataFrameSpec.id}] : [],
      },
      clientStateId,
    }),
    [dataFrameSpec, clientStateId]
  );

  const confirmationButtons: Array<RightSidebarConfirmationButton> = [
    {
      text: 'Cancel',
      onClick: (): void => {
        onChangeSelectedRightSidebarItem(null);
        draftStepGlobalState.deleteState(originalStep.id);
        visualizationConfigState.deleteState(clientStateId);
      },
      isPrimary: originalStep.stepType !== PipelineStepType.CHART_VIEW,
      dataTestAttrsElement: 'cancelButton',
    },
  ];
  if (
    originalStep.stepType === PipelineStepType.CHART_VIEW ||
    originalStep.stepType === PipelineStepType.TABLE_VIEW
  ) {
    confirmationButtons.push({
      text: 'Save',
      isPrimary: true,
      onClick: async (): Promise<void> => {
        const draftStepType = draftStep?.stepType;
        const hasStepTypeChanged = draftStepType != null && draftStepType !== originalStep.stepType;

        if (hasStepTypeChanged && dataFrameSpec != null) {
          // Step type changed via chart type switcher (e.g. chart→table or table→chart)
          await withLoadingScreen(
            saveChartOrTable({
              analysis,
              originalChartOrTableStep: originalStep as PipelineStep<
                PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW
              >,
              newChartConfig:
                draftStepType === PipelineStepType.CHART_VIEW
                  ? (draftStep?.config as PipelineStepConfig<PipelineStepType.CHART_VIEW, 0> | undefined)
                  : undefined,
              newStepType: draftStepType as PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
              inputDataFrameId: dataFrameSpec.id,
              onChangeSelectedRightSidebarItem,
              onChangeSelectedItem,
              selectedRightSidebarItem,
            }),
            'Saving changes...'
          );
        } else if (
          isAnalysisOnlyChartStep(originalStep) ||
          (draftStep != null && isAnalysisOnlyChartStep(draftStep))
        ) {
          // Analysis-only chart step (same step type, just config change)
          await withLoadingScreen(
            PipelineDAO.updateAndRunPipelineStep(analysis.id, {
              stepId: originalStep.id,
              stepConfig: draftStep?.config ?? {},
            })
          );
        } else if (nullableVisualizationConfig != null && dataFrameSpec != null) {
          await withLoadingScreen(
            saveChartOrTable({
              analysis,
              originalChartOrTableStep: originalStep as PipelineStep<
                PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW
              >,
              newChartConfig: {
                chartConfig: getPersistedChartViewConfig(nullableVisualizationConfig),
                chartName: originalStep.config.chartName,
              },
              newVisualizationConfig: nullableVisualizationConfig,
              inputDataFrameId: dataFrameSpec.id,
              onChangeSelectedRightSidebarItem,
              onChangeSelectedItem,
              selectedRightSidebarItem,
            }),
            'Saving changes...'
          );
        }
        onChangeSelectedRightSidebarItem(null);
        visualizationConfigState.deleteState(clientStateId);
        draftStepGlobalState.deleteState(originalStep.id);
      },
      disabled: isDisabled,
      dataTestAttrsElement: 'saveButton',
    });
  }

  const step = draftStep != null && draftStep.config != null ? draftStep : originalStep;
  return (
    <RightSidebarSkeleton
      title={getTitleForView(view)}
      width={RightSidebarWidth.STANDARD}
      confirmationButtons={confirmationButtons}
      dataTestAttrs={{
        component: 'EditViewRightSidebar',
        element: 'root',
      }}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <EditViewRawData
          analysis={analysis}
          clientStateId={clientStateId}
          step={step}
          originalStep={originalStep}
          view={view}
          onChangeVisualizationConfig={onChangeVisualizationConfig}
          nullableVisualizationConfig={nullableVisualizationConfig}
          isDisabled={isDisabled}
          setDraftStep={setDraftStep}
        />
        <EditViewTransforms analysis={analysis} view={view} dataFrameSpec={dataFrameSpec} />
      </StepConfigEditorContext.Provider>
    </RightSidebarSkeleton>
  );
}

interface EditViewRawDataProps {
  analysis: PipelineGraphQL;
  clientStateId: string;
  step: PipelineStep;
  originalStep: PipelineStep;
  view: PipelineViewGraphQL;
  onChangeVisualizationConfig: (newVisualizationConfig: VisualizationConfig) => void;
  nullableVisualizationConfig: VisualizationConfig | null;
  isDisabled: boolean;
  setDraftStep: (step: PipelineStep) => void;
}

function getVisualizationConfig(step: PipelineStep): VisualizationConfig {
  if (step.stepType === PipelineStepType.CHART_VIEW) {
    // This assert is needed for the step.config type
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step);
    const {visualizationConfig} = getVisualizationConfigGQLFromChartStepConfig({
      config: step.config,
      id: step.id,
    });
    return visualizationConfig;
  }
  assert(step.stepType === PipelineStepType.TABLE_VIEW, 'Expected step type to be TABLE_VIEW');
  assertPipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, step);
  const {visualizationConfig} = getVisualizationConfigGQLForTableViewStep(step.id, step.config!);
  return visualizationConfig;
}

/**
 * Returns the dataFrameColumns for the terminal step of a view, if available.
 */
function getDataFrameColumnsForStep(step: PipelineStep): DataFrameColumns | null {
  if (step.stepType === PipelineStepType.CHART_VIEW || step.stepType === PipelineStepType.TABLE_VIEW) {
    try {
      const dataFrame = getSingleInputRawData<
        PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
        0,
        PipelineStepDataKind.DATASET
      >(step as PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW, 0>);
      return dataFrame.columns;
    } catch (err) {
      // getSingleInputRawData asserts when input raw data is empty (e.g. "no data" views).
      // Return null so callers degrade gracefully instead of crashing before the early return.
      if (err instanceof Error && err.message.includes('has no raw output datas')) {
        return null;
      }
      throw err;
    }
  }
  return null;
}

/** @package */
export function EditViewRawData({
  analysis,
  clientStateId,
  step,
  originalStep,
  view,
  onChangeVisualizationConfig,
  nullableVisualizationConfig,
  isDisabled,
  setDraftStep,
}: EditViewRawDataProps): JSX.Element | null {
  const lastNode = view.lineage[view.lineage.length - 1];

  const dataFrameColumns = getDataFrameColumnsForStep(step) ?? getDataFrameColumnsForStep(originalStep);

  const handleSwitchToTable = useCallback((): void => {
    const name = pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalStep)
      ? originalStep.config?.chartName
      : pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, originalStep)
        ? originalStep.config?.viewName
        : undefined;
    setDraftStep({
      ...originalStep,
      stepType: PipelineStepType.TABLE_VIEW,
      config: {
        viewName: name,
      },
    });
  }, [originalStep, setDraftStep]);

  const handleSwitchToChart = useCallback(
    (newType: AnalysisVisualizationType): void => {
      if (dataFrameColumns == null) {
        return;
      }
      const chartOptionType =
        ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE[
          newType as Exclude<
            AnalysisVisualizationType,
            AnalysisVisualizationType.TABLE | AnalysisVisualizationType.CONTOUR_PLOT
          >
        ];
      const defaultConfig = getDefaultChartConfig(chartOptionType, dataFrameColumns);
      const name = pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, originalStep)
        ? originalStep.config?.viewName
        : pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalStep)
          ? originalStep.config?.chartName
          : undefined;
      setDraftStep({
        ...originalStep,
        stepType: PipelineStepType.CHART_VIEW,
        config: {
          ...defaultConfig,
          chartName: name ?? defaultConfig.chartName,
        },
      });
    },
    [originalStep, setDraftStep, dataFrameColumns]
  );

  if (lastNode.stepOutputReference == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="This view has no data." />;
  }

  // Old path: visualization-backed charts/tables (non-analysis-only)
  if (
    [PipelineStepType.CHART_VIEW, PipelineStepType.TABLE_VIEW].includes(step.stepType) &&
    !isAnalysisOnlyChartStep(step) &&
    !(step.stepType === PipelineStepType.TABLE_VIEW && getAnalysisOnlyChartViewTypes().length > 1)
  ) {
    const dataFrame = tryGetSingleInputRawData<
      PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
      0,
      PipelineStepDataKind.DATASET
    >(step as PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW, 0>);
    const editorIsDisabled = isDisabled || dataFrame == null;

    const visualizationConfig = getVisualizationConfig(step);
    return (
      <VisualizationConfigEditor
        columns={dataFrame?.columns ?? []}
        typeSelectorIsDisabled={editorIsDisabled}
        disabledVisualizationTypes={[]}
        isAnalysisTool={true}
        onChangeVisualizationConfig={editorIsDisabled ? noop : onChangeVisualizationConfig}
        visualizationConfig={nullableVisualizationConfig ?? visualizationConfig}
        useSystemNames={true}
        enableAnalysisTab={false}
        enableAggregation={false}
        enableFormatTab={undefined}
        enableFormatTabForLineChart={true}
        enableErrorBarColumn={true}
        isDisabled={editorIsDisabled}
      />
    );
  }

  // New path: analysis-only TABLE_VIEW with chart type switcher
  // When the feature gate is on, TABLE_VIEW goes through the new path with chart type switcher
  if (step.stepType === PipelineStepType.TABLE_VIEW && getAnalysisOnlyChartViewTypes().length > 1) {
    return (
      <ChartTypeSwitcher
        currentType={AnalysisVisualizationType.TABLE}
        onChange={(newType) => {
          if (newType !== AnalysisVisualizationType.TABLE) {
            handleSwitchToChart(newType);
          }
        }}
        isDisabled={isDisabled}
      />
    );
  }

  // New path: analysis-only CHART_VIEW
  if (step.stepType === PipelineStepType.CHART_VIEW) {
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step);
    return (
      <EditViewChart
        analysis={analysis}
        clientStateId={clientStateId}
        step={step}
        view={view}
        isDisabled={isDisabled}
        dataFrameColumns={dataFrameColumns ?? []}
        onSwitchToTable={handleSwitchToTable}
      />
    );
  } else {
    return null;
  }
}
